<template>
  <div v-if="!isMobile" class="w-full bg-white overflow-x-hidden">
    <!-- Hero Section -->
    <section class="h-[50vh] relative bg-gradient-to-r from-[#0D0971] to-[#1a1a8e] bg-cover bg-center">
      <div class="absolute inset-0 bg-black/20"></div>
      <div class="relative z-10 h-full flex flex-col justify-center items-center text-center px-[9%]">
        <h1 class="font-bold text-[3.5vw] text-white mb-[1vw]">
          {{ categoryData?.title || 'Category Details' }}
        </h1>
        <div class="w-[20vw] h-[0.3vw] bg-white/50 mb-[2vw]"></div>
        <button
          @click="goBack"
          class="mt-[2vw] px-[2vw] py-[0.8vw] bg-white text-[#0D0971] font-semibold text-[1.1vw] rounded-lg hover:bg-gray-100 transition-colors duration-300 flex items-center gap-[0.5vw]">
          <svg class="w-[1.2vw] h-[1.2vw]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
          </svg>
          {{ t('categoryDetail.backToSourceProduct') }}
        </button>
      </div>
    </section>

    <!-- Content Section -->
    <section class="py-[4vw]">
      <div class="w-[81vw] mx-auto">
        <!-- Description -->
        <div class="mb-[3vw]">
          <div v-if="Array.isArray(categoryData?.description)" class="space-y-[1.2vw]">
            <p v-for="(paragraph, index) in categoryData.description"
               :key="index"
               class="text-[1.2vw] text-[#444] leading-[1.8]">
              {{ paragraph }}
            </p>
          </div>
          <p v-else class="text-[1.2vw] text-[#444] leading-[1.8]">
            {{ categoryData?.description }}
          </p>
        </div>

        <!-- Two Column Layout for Applications and Certifications -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-[3vw] mb-[3vw]">
          <!-- Applications -->
          <div v-if="categoryData?.applications" class="flex flex-col">
            <h2 class="text-[2.2vw] font-bold text-[#0D0971] mb-[1.5vw] flex items-center gap-[0.8vw]">
              <svg class="w-[2vw] h-[2vw]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
              </svg>
              {{ t('categoryDetail.applications') }}
            </h2>
            <div v-if="Array.isArray(categoryData.applications)" class="bg-white rounded-xl shadow-md p-[2vw] border border-gray-100 flex-1">
              <ul class="space-y-[0.8vw]">
                <li v-for="(app, index) in categoryData.applications"
                    :key="index"
                    class="flex items-start gap-[0.8vw] text-[1.1vw] text-[#444] hover:text-[#0D0971] transition-colors duration-200">
                  <span class="text-[#0D0971] font-bold mt-[0.2vw]">•</span>
                  <span>{{ app }}</span>
                </li>
              </ul>
            </div>
            <div v-else class="p-[2vw] bg-gradient-to-br from-[#f3F8FF] to-[#e8f0fe] rounded-lg border-l-4 border-[#0D0971] flex-1">
              <p class="text-[1.2vw] text-[#444] leading-[1.8]">
                {{ categoryData.applications }}
              </p>
            </div>
          </div>

          <!-- Certifications -->
          <div v-if="categoryData?.certifications" class="flex flex-col">
            <h2 class="text-[2.2vw] font-bold text-[#0D0971] mb-[1.5vw] flex items-center gap-[0.8vw]">
              <svg class="w-[2vw] h-[2vw]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
              </svg>
              {{ t('categoryDetail.certifications') }}
            </h2>
            <div class="bg-white rounded-xl shadow-md p-[2vw] border border-gray-100 flex-1">
              <ul class="space-y-[0.8vw]">
                <li v-for="(cert, index) in categoryData.certifications"
                    :key="index"
                    class="flex items-start gap-[0.8vw] text-[1.1vw] text-[#444] hover:text-[#0D0971] transition-colors duration-200">
                  <svg class="w-[1.2vw] h-[1.2vw] text-green-600 flex-shrink-0 mt-[0.2vw]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                  </svg>
                  <span>{{ cert }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Features (if exists) -->
        <div v-if="categoryData?.features" class="mb-[3vw]">
          <h2 class="text-[2.2vw] font-bold text-[#0D0971] mb-[1.5vw] flex items-center gap-[0.8vw]">
            <svg class="w-[2vw] h-[2vw]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/>
            </svg>
            {{ t('categoryDetail.keyFeatures') }}
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-[1.5vw]">
            <div
              v-for="(feature, index) in categoryData.features"
              :key="index"
              class="flex items-start gap-[1vw] p-[1.5vw] bg-[#f3F8FF] rounded-lg hover:shadow-md transition-shadow duration-300">
              <div class="flex-shrink-0 w-[2vw] h-[2vw] bg-[#0D0971] rounded-full flex items-center justify-center mt-[0.2vw]">
                <svg class="w-[1.2vw] h-[1.2vw] text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                </svg>
              </div>
              <p class="text-[1.1vw] text-[#444] leading-[1.6] flex-1">{{ feature }}</p>
            </div>
          </div>
        </div>

        <!-- Sections -->
        <div v-if="categoryData?.sections && categoryData.sections.length > 0">
          <div v-for="(section, sectionIndex) in categoryData.sections" :key="sectionIndex" class="mb-[3vw]">
            <!-- Section Title -->
            <h2 v-if="section.title" class="text-[2.8vw] font-bold text-[#0D0971] mb-[2vw] flex items-center gap-[0.8vw]">
              <span class="text-[2vw]">{{ sectionIndex + 1 }} |</span>
              {{ section.title }}
            </h2>
            <h2 v-else class="text-[2.8vw] font-bold text-[#0D0971] mb-[2vw] flex items-center gap-[0.8vw]">
              <span class="text-[2vw]">{{ sectionIndex + 1 }} |</span>
              {{ t('categoryDetail.productIntroductions') }}
            </h2>

            <!-- Section Description -->
            <p v-if="section.description" class="text-[1.2vw] text-[#444] leading-[1.8] mb-[2vw]">
              {{ section.description }}
            </p>

            <!-- Products -->
            <div v-if="section.products && section.products.length > 0">
              <div v-for="(product, index) in section.products" :key="index" class="mb-[3vw]">
                <!-- Product Name -->
                <h3 class="text-[1.8vw] font-bold text-black mb-[1vw]">{{ product.name }}</h3>

                <!-- Product Description -->
                <p v-if="product.description" class="text-[1.2vw] text-[#444] leading-[1.8] mb-[1.5vw]">
                  {{ product.description }}
                </p>

                <!-- Features and Photos Table -->
                <div class="border border-gray-300 rounded-lg overflow-hidden">
                  <!-- Table Header -->
                  <div class="grid grid-cols-2 bg-[#0D0971]">
                    <div class="px-[2vw] py-[1vw] text-white text-[1.4vw] font-bold text-center border-r border-white">
                      {{ t('categoryDetail.tableHeaders.features') }}
                    </div>
                    <div class="px-[2vw] py-[1vw] text-white text-[1.4vw] font-bold text-center">
                      {{ t('categoryDetail.tableHeaders.photos') }}
                    </div>
                  </div>

                  <!-- Table Body -->
                  <div class="grid grid-cols-2">
                    <!-- Features Column -->
                    <div class="px-[2vw] py-[2vw] bg-[#f5f5f5] border-r border-gray-300">
                      <ul class="space-y-[1vw]">
                        <li v-for="(feature, fIndex) in product.features"
                            :key="fIndex"
                            class="flex items-start gap-[0.8vw] text-[1.1vw] text-black leading-[1.6]">
                          <span class="text-[#0D0971] font-bold mt-[0.2vw]">•</span>
                          <span>{{ feature }}</span>
                        </li>
                      </ul>
                    </div>

                    <!-- Photos Column -->
                    <div class="px-[2vw] py-[2vw] bg-white">
                      <div v-if="product.photos && product.photos.length > 0"
                           :class="product.photos.length === 1 ? 'flex justify-center' : 'grid grid-cols-2 gap-[1vw]'">
                        <div v-for="(photo, pIndex) in product.photos"
                             :key="pIndex"
                             class="flex items-center justify-center bg-gray-100 rounded-lg overflow-hidden aspect-square">
                          <img :src="photo"
                               :alt="`${product.name} - Photo ${pIndex + 1}`"
                               class="w-full h-full object-contain hover:scale-105 transition-transform duration-300"
                               @error="handleImageError">
                        </div>
                      </div>
                      <div v-else class="flex items-center justify-center h-full text-[1.2vw] text-gray-400">
                        {{ t('categoryDetail.notAvailable') }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Key Components -->
            <div v-if="section.keyComponents && section.keyComponents.length > 0" class="mt-[3vw]">
              <h3 class="text-[2vw] font-bold text-black mb-[1.5vw]">{{ t('categoryDetail.keyComponents') }}</h3>

              <div class="border border-gray-300 rounded-lg overflow-hidden">
                <!-- Table Header -->
                <div class="grid bg-[#0D0971]" :class="keyComponentsGridCols">
                  <div class="px-[1.5vw] py-[1vw] text-white text-[1.4vw] font-bold text-center border-r border-white">
                    {{ t('categoryDetail.tableHeaders.component') }}
                  </div>
                  <div class="px-[1.5vw] py-[1vw] text-white text-[1.4vw] font-bold text-center border-r border-white">
                    {{ t('categoryDetail.tableHeaders.description') }}
                  </div>
                  <div class="px-[1.5vw] py-[1vw] text-white text-[1.4vw] font-bold text-center border-r border-white">
                    {{ t('categoryDetail.tableHeaders.typicalUse') }}
                  </div>
                  <div class="px-[1.5vw] py-[1vw] text-white text-[1.4vw] font-bold text-center">
                    {{ t('categoryDetail.tableHeaders.photos') }}
                  </div>
                </div>

                <!-- Table Body -->
                <div v-for="(component, cIndex) in section.keyComponents" :key="cIndex"
                     class="grid border-b border-gray-300 last:border-b-0" :class="keyComponentsGridCols">
                  <!-- Component Name -->
                  <div class="px-[1.5vw] py-[2vw] bg-white border-r border-gray-300 flex items-start">
                    <p class="text-[1.2vw] font-bold text-black">{{ component.name }}</p>
                  </div>

                  <!-- Description -->
                  <div class="px-[1.5vw] py-[2vw] bg-white border-r border-gray-300 flex items-start">
                    <p class="text-[1.1vw] text-[#444] leading-[1.6]">{{ component.description }}</p>
                  </div>

                  <!-- Typical Use -->
                  <div class="px-[1.5vw] py-[2vw] bg-white border-r border-gray-300 flex items-start">
                    <p class="text-[1.1vw] text-[#444] leading-[1.6]">{{ component.typicalUse }}</p>
                  </div>

                  <!-- Photos -->
                  <div class="px-[1.5vw] py-[2vw] bg-[#f5f5f5]">
                    <div v-if="component.photos && component.photos.length > 0"
                         class="grid grid-cols-1 gap-[0.8vw]">
                      <div v-for="(photo, pIndex) in component.photos"
                           :key="pIndex"
                           class="flex items-center justify-center bg-gray-100 rounded-lg overflow-hidden aspect-square">
                        <img :src="photo"
                             :alt="`${component.name} - Photo ${pIndex + 1}`"
                             class="w-full h-full object-contain hover:scale-105 transition-transform duration-300"
                             @error="handleImageError">
                      </div>
                    </div>
                    <div v-else class="flex items-center justify-center h-full text-[1.2vw] text-gray-400">
                      {{ t('categoryDetail.notAvailable') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Solutions -->
            <div v-if="section.solutions && section.solutions.length > 0">
              <div class="border border-gray-300 rounded-lg overflow-hidden">
                <!-- Table Header -->
                <div class="grid grid-cols-2 bg-[#0D0971]">
                  <div class="px-[2vw] py-[1vw] text-white text-[1.4vw] font-bold text-center border-r border-white">
                    {{ t('categoryDetail.tableHeaders.solution') }}
                  </div>
                  <div class="px-[2vw] py-[1vw] text-white text-[1.4vw] font-bold text-center">
                    {{ t('categoryDetail.tableHeaders.introductoryText') }}
                  </div>
                </div>

                <!-- Table Body -->
                <div v-for="(solution, sIndex) in section.solutions" :key="sIndex"
                     class="grid grid-cols-2 border-b border-gray-300 last:border-b-0">
                  <!-- Solution Name -->
                  <div class="px-[2vw] py-[1.5vw] bg-[#f5f5f5] border-r border-gray-300">
                    <p class="text-[1.2vw] font-bold text-black">{{ solution.name }}</p>
                  </div>

                  <!-- Solution Description -->
                  <div class="px-[2vw] py-[1.5vw] bg-white">
                    <p class="text-[1.1vw] text-[#444] leading-[1.6]">{{ solution.description }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Call to Action -->
        
      </div>
    </section>

    <!-- Category Not Found -->
    <section v-if="!categoryData" class="py-[8vw]">
      <div class="w-[81vw] mx-auto text-center">
        <h2 class="text-[2.5vw] font-bold text-[#0D0971] mb-[2vw]">{{ t('categoryDetail.categoryNotFound') }}</h2>
        <p class="text-[1.3vw] text-[#666] mb-[3vw]">
          {{ t('categoryDetail.categoryNotFoundMessage') }}
        </p>
        <button
          @click="goBack"
          class="px-[2.5vw] py-[1vw] bg-[#0D0971] text-white font-semibold text-[1.2vw] rounded-lg hover:bg-[#1a1a8e] transition-colors duration-300">
          {{ t('categoryDetail.backToSourceProduct') }}
        </button>
      </div>
    </section>
  </div>

  <!-- Mobile Version -->
  <MobileCategoryDetail v-if="isMobile" />
</template>

<script lang="ts" setup>
import { computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from 'vue-i18n';
import { useHead } from '@vueuse/head';
import { pushToDataLayer } from '../utils/gtm';
import { useChatWidget } from '../utils/chatWidget';
import { useDeviceDetection } from "@/utils/deviceDetection";
import MobileCategoryDetail from "../components/MobileCategoryDetail.vue";
import { getSEO } from '../utils/seoConfig';

const route = useRoute();
const router = useRouter();
const { locale, messages, t } = useI18n();
const { isMobile } = useDeviceDetection();

// Dynamic grid columns based on locale for Key Components table
const keyComponentsGridCols = computed(() => {
  return locale.value === 'pl' ? 'grid-cols-[1.5fr_2fr_1.5fr_2fr]' : 'grid-cols-[1fr_2fr_1.5fr_2fr]';
});

// Get Fan category data from i18n (hardcoded to 'fans' category)
const categoryData = computed(() => {
  const categories = messages.value[locale.value]?.sourceProduct?.categorys || [];
  const fanCategory = categories.find((cat: any) => cat.id === 'fans');
  return fanCategory?.detailContent || null;
});

// Get category title for meta
const categoryTitle = computed(() => {
  const categories = messages.value[locale.value]?.sourceProduct?.categorys || [];
  const fanCategory = categories.find((cat: any) => cat.id === 'fans');
  return fanCategory?.text || 'Fan Category Details';
});

// 动态多语言 SEO
const seo = computed(() => getSEO(locale.value, 'fans'));

useHead({
  title: computed(() => seo.value.title),
  meta: [
    { name: 'description', content: computed(() => seo.value.description) },
    { name: 'keywords', content: computed(() => seo.value.keywords) },
    { property: 'og:title', content: computed(() => seo.value.title) },
    { property: 'og:description', content: computed(() => seo.value.description) },
  ],
});

onMounted(() => {
  pushToDataLayer('page_view', {
    page_location: window.location.href,
    page_title: document.title,
    page_referrer: document.referrer,
  });
});

const goBack = () => {
  router.push('/core-offers/source-product');
  pushToDataLayer('click', {
    category: 'CategoryDetail',
    label: 'Back to Source Product',
  });
};

const { openChat } = useChatWidget();

const goToContactUs = () => {
  openChat("CategoryDetail");
  pushToDataLayer('click', {
    category: 'CategoryDetail',
    label: 'Contact Us',
  });
};

// Handle image loading errors
const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement;
  img.style.display = 'none';
};
</script>

<style scoped>
/* Add any additional custom styles here */
.inquery {
  position: fixed;
  right: 10px;
  top: 46%;
  z-index: 100;
  width: 5%;
}
</style>

